import { memo, useEffect, useState } from 'react';
import { Box, IconButton, Tooltip, useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import clsx from 'clsx';
import { useSQuadrantStyles } from './style';
import { BarIcon } from '../sicons';
import SText from '../sui/stext';
import { SRoundedContainer } from '../sui/srounded-container';

/**
 * Widest viewport that still offers the collapse toggle. Above it the 320px
 * panel and the results both have room, so the control would be noise.
 *
 * Because the toggle isn't rendered above this width, crossing back up has to
 * force the panel open again — otherwise a panel collapsed on a narrow window
 * would stay shut after maximising, with no control left to reopen it.
 */
const COLLAPSIBLE_MAX_WIDTH = 1380;

interface TopContent {
  title?: string;
  content?: JSX.Element;
}

/**
 * Passed to a `header` render function so the header can host the side panel's
 * collapse toggle. The layout owns the state; the header only decides where the
 * button goes, because only it knows what the button should sit beside.
 */
export interface SQuadrantHeaderControls {
  isCollapsed: boolean;
  toggleCollapsed: () => void;
  /** Ready-made toggle — render it wherever it belongs in the header. */
  collapseToggle: JSX.Element | null;
}

interface SQuadrantLayoutProps {
  defaultCollapsed?: boolean;
  showCollapseButton?: boolean;
  invertedRow?: boolean;
  topLeft?: TopContent;
  topRight?: TopContent;
  bottomLeft: JSX.Element;
  bottomRight: JSX.Element;
  /**
   * A plain element, or a function given {@link SQuadrantHeaderControls} when
   * the header wants to place the collapse toggle itself.
   */
  header?: JSX.Element | ((controls: SQuadrantHeaderControls) => JSX.Element);
}

const SQuadrantLayout = ({
  defaultCollapsed = false,
  showCollapseButton = true,
  invertedRow = false,
  topLeft,
  topRight,
  bottomLeft,
  bottomRight,
  header
}: SQuadrantLayoutProps) => {
  const classes = useSQuadrantStyles();
  const theme = useTheme();
  // `noSsr` on both so the first paint already knows the viewport, instead of
  // flashing the panel open and then shutting it.
  const canCollapse = useMediaQuery(
    theme.breakpoints.down(COLLAPSIBLE_MAX_WIDTH),
    { noSsr: true }
  );
  // The side panel is a fixed 320px, which on a tablet is most of the usable
  // width and leaves almost nothing for the results next to it — so there it
  // also *starts* closed rather than merely being closable (DEV-726).
  const isTabletDown = useMediaQuery(theme.breakpoints.down('md'), {
    noSsr: true
  });
  const [isCollapsed, setIsCollapsed] = useState(
    defaultCollapsed || isTabletDown
  );

  useEffect(() => {
    // Above the collapsible width the toggle is gone, so the panel must never
    // be left shut — only the host's own `defaultCollapsed` still applies, and
    // that one clears itself.
    setIsCollapsed(defaultCollapsed || (canCollapse && isTabletDown));
  }, [defaultCollapsed, canCollapse, isTabletDown]);

  // The chevron points the way the panel will move on click — it folds away
  // from the content, so an inverted row (panel on the right) flips it.
  const chevronPointsLeft = invertedRow ? isCollapsed : !isCollapsed;
  const collapseLabel = isCollapsed ? 'Show filters' : 'Hide filters';
  const toggleCollapsed = () => setIsCollapsed(collapsed => !collapsed);

  // Only built on a viewport narrow enough to need it, where there is a panel
  // to fold and the host wants the affordance; the header decides where it
  // actually gets rendered.
  const collapseToggle =
    canCollapse && showCollapseButton && !!bottomLeft ? (
      <Tooltip title={collapseLabel} placement="bottom" arrow>
        <IconButton
          size="small"
          aria-label={collapseLabel}
          aria-expanded={!isCollapsed}
          className={classes.collapseButton}
          onClick={toggleCollapsed}>
          {chevronPointsLeft ? (
            <ChevronLeftIcon fontSize="small" />
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    ) : null;

  return (
    <Box className={classes.container}>
      {typeof header === 'function'
        ? header({ isCollapsed, toggleCollapsed, collapseToggle })
        : header}
      <Box className={classes.grid}>
        {!isCollapsed && (
          <Box
            style={{ maxWidth: 320, minWidth: 320 }}
            className={clsx({
              [classes.left]: !invertedRow,
              [classes.right]: invertedRow,
              [classes.order2]: invertedRow,
              [classes.gridItem]: true
            })}>
            {topLeft && (
              <Box className={classes.topConatiner}>
                {topLeft?.title && (
                  <SText weight="bold" title={topLeft.title} />
                )}
                {topLeft?.content || null}
              </Box>
            )}
            {!!bottomLeft && (
              <SRoundedContainer className={classes.bottomContainer}>
                {bottomLeft}
              </SRoundedContainer>
            )}
          </Box>
        )}

        <Box className={classes.gridItem}>
          {topRight && (
            <Box className={classes.topConatiner}>
              {showCollapseButton && (
                <IconButton onClick={toggleCollapsed}>
                  <BarIcon />
                </IconButton>
              )}
              {topRight?.title && (
                <SText weight="bold" title={topRight.title} />
              )}
              {topRight?.content || null}
            </Box>
          )}
          <SRoundedContainer className={classes.bottomContainer}>
            {bottomRight}
          </SRoundedContainer>
        </Box>
      </Box>
    </Box>
  );
};

export default memo(SQuadrantLayout);
